import {
  AttentionDot,
  Avatar,
  Button,
  Chip,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ParsleyLockup
} from '@parsleyai/ui'
import { Bell, CircleHelp, CreditCard, Gift, LogOut, Newspaper, Settings, UserRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import FeedbackTrigger from '@/components/feedback/FeedbackTrigger'
import HeaderUndoControl from '@/components/HeaderUndoControl'
import InviteFriendDialog from '@/components/invite/InviteFriendDialog'
import { canInvite, deriveInviteAllowance } from '@/components/invite/inviteQuota'
import NavTabs from '@/components/NavTabs'
import { PlanPill } from '@/components/planGen/PlanPill'
import ThemeToggle from '@/components/ThemeToggle'
import { useIsMobile } from '@/lib/useIsMobile'
import { cn } from '@/lib/utils'
import { useWhatsNewIndicator } from '@/pages/whatsNew/useWhatsNewIndicator'
import { useAuth, useProfile } from '@/store'

// Overlay (lg+): see-through over a page's photo. The bar re-points its own ink, card, border and green
// tokens to on-photo values, so every control inside reads on the photo without its own variant. The
// active tab's white comes from the tabs alone: the lockup's leaf is brand green and must stay so.
const OVERLAY =
  'lg:border-transparent lg:bg-transparent lg:backdrop-blur-none lg:[--foreground:1_0_0] lg:[--muted-foreground:0.9_0.01_140] lg:[--text-faint:0.82_0.01_140] lg:[--card:0.2667_0.0375_154.4] lg:[--border:0.42_0.03_154] lg:[--brand-green-border:0.55_0.04_154] lg:[--gray-50:0.2667_0.0375_154.4] lg:[--gray-800:0.2667_0.0375_154.4] lg:[--brand-green-ink:1_0_0] lg:[--brand-green-50:0.2667_0.0375_154.4]'

export default function Navbar({ overlay = false }: { overlay?: boolean }) {
  const { isAuthenticated, signOut, user } = useAuth()
  const { capabilities, inviteQuota } = useProfile()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [inviteOpen, setInviteOpen] = useState(false)
  // PlanPill also lives in GhostTopBar (mobile). Both nav bars are always mounted (visibility is
  // CSS-only), so render the pill in exactly one of them — its popover state is now shared via the
  // provider, and a second, hidden mount would fight the visible one's click-outside handling.
  const isMobile = useIsMobile()
  const { unseen: whatsNewUnseen } = useWhatsNewIndicator()

  const showInvite = canInvite(capabilities)
  const invitesLeft = deriveInviteAllowance(capabilities, inviteQuota, Date.now()).remaining

  const navItems = [
    { path: '/meal-plans', label: t('nav.mealPlans'), exact: false },
    { path: '/recipes', label: t('nav.recipes'), exact: true },
    { path: '/eating-style', label: t('nav.myDiet'), exact: false }
  ]

  return (
    <>
      {/* ── Global sticky header (md+): translucent blur bar, 66px, 44px gutters.
          Mobile uses GhostTopBar instead. See docs handoff: design_handoff_header_update. */}
      <nav
        className={cn(
          'sticky top-0 z-40 hidden h-[66px] items-center border-b border-foreground/[0.08] bg-background/[0.82] px-11 backdrop-blur-nav transition-[background-color,border-color] duration-200 motion-reduce:transition-none md:flex',
          overlay && OVERLAY
        )}>
        <div className="flex-1">
          <Link
            to="/home"
            className="flex w-fit items-center rounded-md focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <ParsleyLockup className={cn(overlay && 'lg:hidden')} />
            {overlay && <ParsleyLockup tone="inverse" className="hidden lg:flex" />}
          </Link>
        </div>

        {isAuthenticated ? (
          <>
            <div className={cn(overlay && 'lg:[--brand-green:1_0_0]')}>
              <NavTabs items={navItems} />
            </div>
            <div className="flex flex-1 items-center justify-end gap-3">
              {!isMobile && <PlanPill />}
              <HeaderUndoControl />
              <FeedbackTrigger />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Avatar
                    interactive
                    src={user?.photoURL}
                    name={user?.displayName}
                    size="md"
                    status={whatsNewUnseen ? 'attention' : undefined}
                    aria-label="Open account menu"
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="min-w-[288px] p-2">
                  <DropdownMenuItem className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl" onSelect={() => navigate('/settings/profile')}>
                    <UserRound className="w-4 h-4" />
                    {t('nav.userMenu.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl"
                    onSelect={() => navigate('/settings/subscription')}>
                    <CreditCard className="w-4 h-4" />
                    {t('nav.userMenu.subscription')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl"
                    onSelect={() => navigate('/settings/notifications')}>
                    <Bell className="w-4 h-4" />
                    {t('nav.userMenu.notifications')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl"
                    onSelect={() => navigate('/settings/preferences')}>
                    <Settings className="w-4 h-4" />
                    {t('nav.userMenu.preferences')}
                  </DropdownMenuItem>
                  {showInvite && (
                    <>
                      <DropdownMenuSeparator className="my-2" />
                      <DropdownMenuItem
                        className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl bg-brand-green-50/55 focus:bg-brand-green-50 dark:bg-gray-800/40 dark:focus:bg-gray-800/70"
                        onSelect={() => setInviteOpen(true)}>
                        <span className="flex size-[30px] items-center justify-center rounded-[9px] bg-brand-green-600 text-white shadow-[0_1px_2px_oklch(var(--brand-green-900)/0.25)]">
                          <Gift className="size-[17px]" />
                        </span>
                        <span className="flex-1">{t('nav.userMenu.invite')}</span>
                        <Chip variant="meta" className="py-1 font-mono font-light text-2xs leading-none">
                          {t('invite.menuBadge', { count: invitesLeft })}
                        </Chip>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="my-2" />
                  <DropdownMenuItem className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl" onSelect={() => navigate('/settings/help')}>
                    <CircleHelp className="w-4 h-4" />
                    {t('nav.userMenu.help')}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl" onSelect={() => navigate('/whats-new')}>
                    <Newspaper className="w-4 h-4" />
                    {t('footer.whatsNew')}
                    {whatsNewUnseen && <AttentionDot label={t('whatsNew.unseenLabel')} />}
                  </DropdownMenuItem>
                  <ThemeToggle menuItem />
                  <DropdownMenuSeparator className="my-2" />
                  <DropdownMenuItem className="gap-3 px-3 py-2.5 cursor-pointer rounded-xl" onSelect={() => signOut()}>
                    <LogOut className="w-4 h-4" />
                    {t('nav.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        ) : (
          <div className="flex-1 flex justify-end">
            <Link to="/login">
              <Button variant="default" size="sm">
                {t('nav.logIn')}
              </Button>
            </Link>
          </div>
        )}
      </nav>
      {showInvite && <InviteFriendDialog open={inviteOpen} onOpenChange={setInviteOpen} />}
    </>
  )
}
